import type { FinanceHealthRatio, FinanceHealthRatioId } from "@ibada/shared-types/finance"

import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { useFinanceHealthQuery } from "@/features/finance/services/finance-query"

import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"

export interface FinancialHealthDataResult {
  /** The evaluated ratio for an id — value, variance and verdict, all decided on the server. */
  ratio: (id: FinanceHealthRatioId) => FinanceHealthRatio | undefined
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
}

/**
 * The financial-health ratios for the overview's scope + period, read from `GET /finance/overview/health`
 * (IBA-117). Nothing is computed here: the ratio, its variance against the previous equal-length period and
 * its good/warning/critical verdict are accounting outputs, produced once on the server from the ledger.
 *
 * Every ratio widget calls this with the same scope/period, so React Query dedupes to a single request — yet
 * each widget keeps its own loading boundary and can fail/refresh independently.
 */
export function useFinancialHealthData(): FinancialHealthDataResult {
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()

  const financeScope = scope.context === "parish" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch
  const enabled = Boolean(
    scope.workspaceId && (scope.context === "parish" || scope.branchId) && period.from && period.to,
  )

  const query = useFinanceHealthQuery(
    enabled && scope.workspaceId
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId,
          scope: financeScope,
          dateFrom: period.from,
          dateTo: period.to,
        }
      : null,
  )

  return {
    ratio: (id) => query.data?.ratios.find((ratio) => ratio.id === id),
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => {
      void query.refetch()
    },
  }
}
